import { useEffect, useState } from 'react'
import { apiAdminGetProviders, apiAdminUpdateProvider, ApiRequestError, type AdminProvider as ProviderRow } from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { useT } from '@/contexts/language'
import type { Dict } from '@/lib/i18n'

function providerTypeLabel(t: Dict, type: string): string {
  const map: Record<string, string> = {
    crypto: t.providers.typeCrypto, forex: t.providers.typeForex, gold: t.providers.typeGold,
    stock: t.providers.typeStock, oil: t.providers.typeOil, index: t.providers.typeIndex,
    custom: t.providers.typeCustom,
  }
  return map[type] ?? type
}

function ProviderCard({
  provider,
  onToggle,
  toggling,
}: {
  provider: ProviderRow
  onToggle: (id: string, isActive: boolean) => void
  toggling: boolean
}) {
  const t = useT()
  return (
    <div className="border border-border rounded-[var(--radius)] bg-card p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold truncate">
              {provider.name}
            </span>
            <Badge variant="outline" className="text-[10px] uppercase shrink-0">
              {providerTypeLabel(t, provider.type)}
            </Badge>
            <Badge
              variant={provider.isActive ? 'success' : 'secondary'}
              className="text-[10px] shrink-0"
            >
              {provider.isActive ? t.common.active : t.common.inactive}
            </Badge>
          </div>
          <span className="font-mono text-xs text-muted-foreground">{provider.code}</span>
        </div>

        <Button
          variant={provider.isActive ? 'outline' : 'default'}
          size="sm"
          disabled={toggling}
          onClick={() => onToggle(provider.id, !provider.isActive)}
          className="shrink-0 gap-1.5"
        >
          {toggling && <RefreshCw className="h-3 w-3 animate-spin" />}
          {provider.isActive ? t.providers.disable : t.providers.enable}
        </Button>
      </div>

      {provider.symbols.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground/60">
            {t.providers.symbols} ({provider.symbols.length})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {provider.symbols.map((sym) => (
              <div
                key={sym.id}
                className={
                  'flex items-center gap-1.5 px-2 py-0.5 rounded-[var(--radius-sm)] border ' +
                  (sym.isActive
                    ? 'border-border bg-muted/30'
                    : 'border-border/40 bg-muted/10 opacity-50')
                }
              >
                <span className="font-mono text-[10px] font-medium">{sym.symbol}</span>
                {sym.displayName && (
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {sym.displayName}
                  </span>
                )}
                <span className="font-mono text-[10px] text-muted-foreground/50">
                  .{sym.decimalPlaces}dp
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {Object.keys(provider.config).length > 0 && (
        <details className="group">
          <summary className="cursor-pointer text-[10px] uppercase tracking-widest text-muted-foreground/60 hover:text-muted-foreground select-none">
            {t.providers.config}
          </summary>
          <pre className="mt-1.5 p-2 rounded-[var(--radius-sm)] bg-muted/40 font-mono text-[10px] text-muted-foreground overflow-x-auto">
            {JSON.stringify(provider.config, null, 2)}
          </pre>
        </details>
      )}
    </div>
  )
}

export default function AdminProvidersPage() {
  const t = useT()
  const [providers, setProviders] = useState<ProviderRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)

  useEffect(() => {
    void fetchProviders()
  }, [])

  async function fetchProviders() {
    setLoading(true)
    setError(null)
    try {
      const data = await apiAdminGetProviders()
      setProviders(data)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }

  async function handleToggle(id: string, isActive: boolean) {
    setTogglingId(id)
    try {
      const updated = await apiAdminUpdateProvider(id, isActive)
      setProviders((prev) => prev?.map((p) => (p.id === updated.id ? updated : p)) ?? null)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.providers.toggleError)
    } finally {
      setTogglingId(null)
    }
  }

  return (
    <div className="flex flex-col gap-4 p-4 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t.providers.title}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t.providers.subtitle}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={loading}
          onClick={() => void fetchProviders()}
          className="gap-1.5"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          {t.common.refresh}
        </Button>
      </div>

      {error && (
        <div
          className="flex items-center gap-2 p-4 border border-border rounded-[var(--radius)]"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      {loading && (
        <div className="flex flex-col gap-3">
          {[0, 1].map((i) => (
            <div key={i} className="border border-border rounded-[var(--radius)] bg-card p-4 flex flex-col gap-3">
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-8 w-20" />
              </div>
              <div className="flex gap-1.5">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-16" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && providers !== null && providers.length === 0 && (
        <div className="border border-border rounded-[var(--radius)] bg-card py-12 text-center">
          <p className="text-sm text-muted-foreground">{t.providers.noProviders}</p>
        </div>
      )}

      {!loading && providers !== null && providers.length > 0 && (
        <div className="flex flex-col gap-3">
          {providers.map((provider) => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              onToggle={(id, isActive) => void handleToggle(id, isActive)}
              toggling={togglingId === provider.id}
            />
          ))}
        </div>
      )}
    </div>
  )
}
